import React, { useEffect, useMemo, useState } from 'react';
import APIService from '../services/api';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';

/**
 * Tiroir « Facture » — l'accès direct à une facture créée (depuis un CRA ou à
 * la main) : voir le détail, l'éditer tant qu'elle est en brouillon, l'envoyer
 * au client, suivre son état. Demain, la même fiche portera la réconciliation
 * Billy (réf. affichée dès que le serveur la publie — rien d'inventé).
 *
 * Routes : PUT /factures/:id (édition brouillon + statut, déjà en place pour
 * le statut), POST /factures/:id/envoyer (email au client, tolérée absente).
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });
const mono = { fontFamily: "'IBM Plex Mono',monospace" };

const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '11px', padding: '14px 16px' };
const LBL = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
const GHOST_BTN = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const PRIMARY_BTN = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 14px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const INPUT = { boxSizing: 'border-box', width: '100%', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '9px 11px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };

const STATUTS = [
  ['brouillon', 'Brouillon', 'mut'],
  ['envoyee', 'Envoyée', 'warn'],
  ['payee', 'Payée', 'ok'],
  ['en_retard', 'En retard', 'bad'],
];
const chip = (s) => STATUTS.find(([k]) => k === s) || STATUTS[0];
const Chip = ({ kind = 'mut', children }) => <span className={`src-chip src-chip--${kind}`}>{children}</span>;
const chfAmt = (n) => `${Number(n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`;
const fmtDate = (iso) => { if (!iso) return ''; const t = new Date(iso); return Number.isNaN(t.getTime()) ? iso : t.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }); };

const routeAbsente = (r) => r.status === 404 || r.status === 405 || r.status === 501;

// Le destinataire de l'envoi : le contact du client porteur du rôle clé
// « Reçoit les factures » dans l'organigramme (même règle que le serveur —
// il reste l'autorité, on ne fait qu'annoncer).
/** Comparaison de noms de société : ni la casse, ni les accents, ni les
 *  espaces en trop ne doivent empêcher de reconnaître le même client. */
const memeNom = (a, b) => {
  const n = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
  return !!n(a) && n(a) === n(b);
};
/** Le rôle clé « Reçoit les factures », sous les écritures qu'il peut prendre. */
const recoitLesFactures = (c) => /re[çc]oit.*factures?/i.test(
  String(c?.key || c?.role_cle || c?.responsabilite || c?.role || c?.fonction || '')
);
/** Le destinataire tel que le SERVEUR le désigne, quand il le désigne. */
const destinataireServeur = (f) => {
  const d = f?.destinataire;
  if (!d) return null;
  if (typeof d === 'string') return /@/.test(d) ? { email: d, nom: '', source: 'serveur' } : null;
  return d.email ? { email: d.email, nom: d.nom || d.name || '', source: 'serveur' } : null;
};
const destinataireDe = (f, clients) => {
  if (!f) return { client: null, contact: null, motif: null };
  const cid = f.client_id ?? f.clientId ?? null;
  const liste = clients || [];
  // L'identifiant d'abord ; le nom ensuite, car une facture ne porte pas
  // toujours client_id — et un nom qui ne diffère que par la casse désignait
  // alors un client « introuvable », donc une adresse redemandée à chaque envoi.
  const client = liste.find(c => cid != null && String(c.id) === String(cid))
    || liste.find(c => memeNom(c.nom || c.name, f.client))
    || null;
  // Ordre de vérité : ce que dit le serveur, puis l'email de facturation de la
  // fiche client (prioritaire par contrat), puis le rôle clé des contacts.
  // Le front ne déduit plus rien tant que le serveur parle.
  const duServeur = destinataireServeur(f);
  const parFiche = client?.email_facturation
    ? { email: client.email_facturation, nom: 'Facturation', source: 'client' }
    : null;
  const parRole = (client?.contacts || []).find(c => recoitLesFactures(c) && c.email) || null;
  const contact = duServeur || parFiche || parRole || null;
  // Le serveur peut expliquer POURQUOI il n'a pas de destinataire : sa phrase
  // vaut mieux que la nôtre.
  const motif = !contact ? (f.destinataire_motif || null) : null;
  return { client, contact, motif };
};

export default function FactureDrawer({ facture: ligne, missions = [], clients = [], onClose, onOpenMission, onStatus, onDownload, onChanged, onClientsChanged, showNotification }) {
  // La ligne de liste ne porte pas tout : le destinataire désigné par le
  // serveur, le motif de son absence et le détail des lignes ne vivent que sur
  // GET /factures/:id. On lit le détail à l'ouverture et on le pose PAR-DESSUS
  // la ligne — route absente ou en échec, le tiroir reste celui de la liste.
  const [detail, setDetail] = useState(null);
  const [relire, setRelire] = useState(0);
  useEffect(() => {
    const id = ligne?.id;
    setDetail(null);
    if (id == null) return undefined;
    let annule = false;
    fetch(`${BASE}/factures/${id}`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (annule || !j) return;
        const d = j.data || j.facture || j;
        if (d && typeof d === 'object' && !Array.isArray(d) && d.id != null) setDetail(d);
      })
      .catch(() => { /* la ligne de liste suffit à afficher le tiroir */ });
    return () => { annule = true; };
  }, [ligne?.id, relire]);
  const f = useMemo(() => (detail ? { ...ligne, ...detail } : ligne), [ligne, detail]);
  const majFacture = () => { setRelire(n => n + 1); onChanged?.(); };
  const brouillon = (f?.statut || 'brouillon') === 'brouillon';
  // Brouillon d'édition : échéance, note, et lignes quand le serveur en donne.
  const [edit, setEdit] = useState(null); // null = lecture
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  // Destinataires : l'adresse vient du rôle clé « Reçoit les factures » de la
  // fiche client. Elle ne se demande QU'À L'ENVOI, dans le popup, et seulement
  // si elle manque — puis elle est retenue sur la fiche client.
  const [popup, setPopup] = useState(null); // { a, cc, retenir } | null
  // Erreur de saisie ACCROCHÉE au champ fautif du popup (bordure + message).
  const [destErreur, setDestErreur] = useState(null); // { champ: 'a'|'cc', message }
  const emailRef = React.useRef(null);
  const ccRef = React.useRef(null);
  useEffect(() => { setEdit(null); }, [f?.id]);

  // Facturiers configurés (affacturage). Route absente : aucun choix proposé,
  // la facture part avec les coordonnées de l'organisation, comme avant.
  const [facturiers, setFacturiers] = useState([]);
  useEffect(() => {
    let annule = false;
    fetch(`${BASE}/config/facturiers`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (annule || !j) return; const a = j.data || j.facturiers || (Array.isArray(j) ? j : []); if (Array.isArray(a)) setFacturiers(a); })
      .catch(() => {});
    return () => { annule = true; };
  }, []);

  const mission = useMemo(() => {
    const mid = f?.mission_id ?? f?.missionId ?? null;
    return mid != null ? missions.find(m => String(m.id) === String(mid)) : null;
  }, [f, missions]);

  // Destinataires : calculés AVANT la garde de rendu (règle des hooks).
  const { client: clientFiche, contact: destinataire, motif: destinataireMotif } = destinataireDe(f, clients);
  useEffect(() => { setPopup(null); setDestErreur(null); }, [f?.id]);

  if (!f) return null;
  const [, sl, sk] = chip(f.statut);
  const lignes = Array.isArray(f.lignes) ? f.lignes : null;
  const tvaTaux = Number(f.tva_taux ?? 8.1);
  const totalHT = Number(f.total_ht || 0);
  const totalTTC = f.total_ttc ?? totalHT * (1 + tvaTaux / 100);
  const envoyeeLe = f.sent_at || f.envoyee_le || f.envoyee_at || null;
  const payeeLe = f.paid_at || f.payee_le || f.payee_at || null;
  const idxStatut = ['brouillon', 'envoyee', 'payee'].indexOf(f.statut === 'en_retard' ? 'envoyee' : (f.statut || 'brouillon'));


  const commencerEdition = () => setEdit({
    echeance: f.echeance ? String(f.echeance).slice(0, 10) : '',
    note: f.note || f.notes || '',
    lignes: lignes ? lignes.map(l => ({ description: l.description || l.label || '', quantite: String(l.quantite ?? l.qty ?? 1), prix_unitaire: String(l.prix_unitaire ?? l.pu ?? '') })) : null,
    facturier_id: f.facturier_id ?? f.facturier?.id ?? '',
  });

  const enregistrer = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const body = {};
      if ((edit.echeance || '') !== String(f.echeance || '').slice(0, 10)) body.echeance = edit.echeance || null;
      if ((edit.note || '') !== String(f.note || f.notes || '')) body.note = edit.note;
      if (edit.lignes) {
        const nettes = edit.lignes
          .map(l => ({ description: String(l.description || '').trim(), quantite: Number(String(l.quantite).replace(',', '.')) || 0, prix_unitaire: Number(String(l.prix_unitaire).replace(',', '.')) || 0 }))
          .filter(l => l.description);
        body.lignes = nettes;
      }
      const facturierActuel = String(f.facturier_id ?? f.facturier?.id ?? '');
      if (String(edit.facturier_id ?? '') !== facturierActuel) {
        body.facturier_id = edit.facturier_id === '' ? null : Number(edit.facturier_id) || edit.facturier_id;
      }
      if (Object.keys(body).length === 0) { setEdit(null); return; }
      const r = await fetch(`${BASE}/factures/${f.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (routeAbsente(r) && !j.error && !j.message) { showNotification?.('L’édition de la facture arrive côté serveur — rien n’a été enregistré.', 'error'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Facture mise à jour.', 'success');
      setEdit(null);
      majFacture();
    } catch (e) { showNotification?.(`Enregistrement impossible : ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  // Envoi au client — action SORTANTE, toujours confirmée. Le PDF part avec
  // les coordonnées de l'organisation ; le statut passera « Envoyée ».
  // Le POST d'envoi + la retenue de l'adresse sur la fiche client.
  const executerEnvoi = async (a, ccList, retenir) => {
    setSending(true);
    try {
      const r = await fetch(`${BASE}/factures/${f.id}/envoyer`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ to: a, ...(ccList.length ? { cc: ccList } : {}) }) });
      const j = await r.json().catch(() => ({}));
      if (routeAbsente(r) && !j.error && !j.message) { showNotification?.('L’envoi par email arrive côté serveur — rien n’est parti. Vous pouvez télécharger le PDF et l’envoyer vous-même.', 'error'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || `Facture ${f.numero || ''} envoyée au client.`, 'success');
      // Retenir l'adresse comme contact « Reçoit les factures » du client —
      // meilleur effort : l'envoi est déjà parti, un échec ici ne le défait
      // pas ; la liste des clients est rafraîchie pour que l'adresse soit
      // connue dès le prochain envoi.
      if (retenir && clientFiche && (!destinataire || destinataire.email !== a)) {
        try {
          // Un champ dédié, prioritaire côté serveur et validé par lui : plus
          // besoin de recomposer la liste des contacts et d'espérer que le rôle
          // clé survive à l'écriture.
          const rc = await fetch(`${BASE}/clients/${clientFiche.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ email_facturation: a }) });
          const jc = await rc.json().catch(() => ({}));
          if (!rc.ok || jc.success === false) throw new Error(jc.error || jc.message || `HTTP ${rc.status}`);
          // Preuve avant annonce : on relit la fiche. Un serveur qui accepte le
          // PUT sans conserver le rôle clé ferait redemander l'adresse à chaque
          // envoi — autant le dire tout de suite plutôt que de le découvrir la
          // fois suivante.
          const verif = await fetch(`${BASE}/clients/${clientFiche.id}`, { headers: authH() })
            .then(r => (r.ok ? r.json() : null)).catch(() => null);
          const fiche = verif ? (verif.data || verif.client || verif) : null;
          const garde = fiche
            ? (String(fiche.email_facturation || '').toLowerCase() === a.toLowerCase()
              || (Array.isArray(fiche.contacts) && fiche.contacts.some(c => recoitLesFactures(c) && String(c.email || '').toLowerCase() === a.toLowerCase())))
            : null;
          if (garde === false) {
            showNotification?.(`${a} n'a pas pu être retenu sur la fiche de ${clientFiche.nom} — l'adresse vous sera redemandée au prochain envoi.`, 'error');
          } else {
            showNotification?.(`${a} retenu comme contact de facturation de ${clientFiche.nom}.`, 'success');
          }
          onClientsChanged?.();
        } catch (e) {
          // L'envoi, lui, est parti : on ne le défait pas — mais on ne laisse
          // pas croire que l'adresse est enregistrée.
          showNotification?.(`Facture envoyée, mais ${a} n'a pas pu être retenu sur la fiche client : ${e.message}`, 'error');
        }
      }
      majFacture();
    } catch (e) { showNotification?.(`Envoi impossible : ${e.message}`, 'error'); }
    finally { setSending(false); }
  };

  // Adresse connue (rôle clé) → confirmation directe, rien à saisir.
  // Adresse absente → le popup la demande, puis la retient sur la fiche.
  const envoyer = async () => {
    if (!destinataire?.email) {
      setDestErreur(null);
      setPopup({ a: '', cc: '', retenir: true });
      setTimeout(() => emailRef.current?.focus(), 120);
      return;
    }
    const ok = await confirmErpia({
      title: f.statut === 'envoyee' ? 'Renvoyer la facture au client' : 'Envoyer la facture au client',
      message: `${f.statut === 'envoyee' ? 'Renvoyer' : 'Envoyer'} la facture ${f.numero || `#${f.id}`} (${chfAmt(totalTTC)}) à ${f.client || 'ce client'} par email ?`,
      detail: `Destinataire : ${[destinataire.nom, destinataire.email].filter(Boolean).join(' — ')} (rôle « Reçoit les factures »). Le PDF part avec les coordonnées de paiement.`,
      confirmLabel: 'Envoyer au client', cancelLabel: 'Annuler', tone: 'primary',
    });
    if (ok) await executerEnvoi(destinataire.email, [], false);
  };

  // Validation du popup : erreurs accrochées aux champs, jamais en toast.
  const envoyerDepuisPopup = async () => {
    const a = (popup?.a || '').trim();
    const ccList = (popup?.cc || '').split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);
    if (!/@/.test(a)) {
      setDestErreur({ champ: 'a', message: 'Saisissez l’email de facturation du client.' });
      setTimeout(() => emailRef.current?.focus(), 60);
      return;
    }
    const mauvaises = ccList.filter(x => !/@/.test(x));
    if (mauvaises.length) {
      setDestErreur({ champ: 'cc', message: `Adresse en copie invalide : ${mauvaises.join(', ')}` });
      setTimeout(() => ccRef.current?.focus(), 60);
      return;
    }
    if (ccList.length > 5) {
      setDestErreur({ champ: 'cc', message: `Au plus 5 adresses en copie — il y en a ${ccList.length}.` });
      setTimeout(() => ccRef.current?.focus(), 60);
      return;
    }
    setDestErreur(null);
    const retenir = popup.retenir;
    setPopup(null);
    await executerEnvoi(a, ccList, retenir);
  };

  const SUIVI = [
    { k: 'brouillon', l: 'Créée', date: f.created_at || f.date_creation || null },
    { k: 'envoyee', l: f.statut === 'en_retard' ? 'Envoyée — en retard' : 'Envoyée au client', date: envoyeeLe },
    { k: 'payee', l: 'Payée', date: payeeLe },
  ];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(8,9,12,0.45)' }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 61, width: 'min(520px,94vw)', background: 'var(--panel)', borderLeft: '1px solid var(--border)', boxShadow: '-18px 0 50px rgba(8,9,12,0.3)', overflowY: 'auto', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '11px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="receipt" style={{ fontSize: '17px' }} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...mono, fontSize: '16px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)' }}>{f.numero || `Facture #${f.id}`}</div>
            <div style={{ marginTop: '1px', fontSize: '12.5px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[f.client, (f.cra_month || f.mois) ? `temps ${f.cra_month || f.mois}` : null].filter(Boolean).join(' · ') || '—'}</div>
          </div>
          <Chip kind={sk}>{sl}</Chip>
          <button onClick={onClose} className="src-row-hover" aria-label="Fermer" style={{ ...GHOST_BTN, width: '32px', height: '32px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" style={{ fontSize: '13px' }} /></button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* ── Suivi de l'état ── */}
          <div style={CARD}>
            <div style={{ ...LBL, marginBottom: '10px' }}>Suivi</div>
            {SUIVI.map((s, i) => {
              const atteint = i <= idxStatut;
              const retard = s.k === 'envoyee' && f.statut === 'en_retard';
              return (
                <div key={s.k} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', flex: 'none', background: retard ? 'var(--danger-fg)' : atteint ? 'var(--accent)' : 'var(--code-bg)', border: atteint ? 'none' : '1px solid var(--hair)' }} />
                  <span style={{ flex: 1, fontSize: '12.5px', fontWeight: atteint ? 700 : 500, color: retard ? 'var(--danger-fg)' : atteint ? 'var(--text-strong)' : 'var(--text-3)' }}>{s.l}</span>
                  {s.date && <span style={{ ...mono, fontSize: '11px', color: 'var(--text-3)' }}>{fmtDate(s.date)}</span>}
                </div>
              );
            })}
            {f.echeance && (
              <div style={{ marginTop: '7px', paddingTop: '9px', borderTop: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-2)' }}>
                <BI icon="calendar-event" style={{ fontSize: '11px' }} /> Échéance <b className="ats-mono" style={{ color: 'var(--text-strong)' }}>{fmtDate(f.echeance)}</b>
              </div>
            )}
            {(f.billy_ref || f.billy_id) && (
              <div style={{ marginTop: '7px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-2)' }} title="Référence de réconciliation dans Billy (comptabilité)">
                <BI icon="link-45deg" style={{ fontSize: '11px' }} /> Réf. Billy <span className="ats-mono" style={{ color: 'var(--text-strong)' }}>{f.billy_ref || f.billy_id}</span>
              </div>
            )}
          </div>

          {/* ── Détail ── */}
          <div style={CARD}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ ...LBL, flex: 1 }}>Détail</span>
              {mission && (
                <button onClick={() => onOpenMission?.(mission)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '11.5px' }}>
                  <BI icon="briefcase" style={{ fontSize: '10px' }} /> {mission.titre}
                </button>
              )}
            </div>
            {lignes && lignes.length > 0 && !edit && (
              <div style={{ marginBottom: '10px' }}>
                {lignes.map((l, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '9px', padding: '5px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px' }}>
                    <span style={{ flex: 1, minWidth: 0, color: 'var(--text)' }}>{l.description || l.label || '—'}</span>
                    <span style={{ ...mono, fontSize: '11.5px', color: 'var(--text-3)', flex: 'none' }}>{l.quantite ?? l.qty ?? 1} × {chfAmt(l.prix_unitaire ?? l.pu)}</span>
                    <span style={{ ...mono, fontSize: '12px', fontWeight: 600, color: 'var(--text-strong)', flex: 'none', width: '96px', textAlign: 'right' }}>{chfAmt((Number(l.quantite ?? l.qty ?? 1) || 0) * (Number(l.prix_unitaire ?? l.pu) || 0))}</span>
                  </div>
                ))}
              </div>
            )}
            {edit && edit.lignes && (
              <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {edit.lignes.map((l, i) => (
                  <div key={i} style={{ display: 'flex', gap: '7px' }}>
                    <input value={l.description} onChange={e => setEdit(prev => ({ ...prev, lignes: prev.lignes.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))}
                      placeholder="Description" style={{ ...INPUT, flex: 2 }} />
                    <input value={l.quantite} onChange={e => setEdit(prev => ({ ...prev, lignes: prev.lignes.map((x, j) => j === i ? { ...x, quantite: e.target.value } : x) }))}
                      type="number" step="0.5" min="0" aria-label="Quantité" style={{ ...INPUT, width: '72px', ...mono }} />
                    <input value={l.prix_unitaire} onChange={e => setEdit(prev => ({ ...prev, lignes: prev.lignes.map((x, j) => j === i ? { ...x, prix_unitaire: e.target.value } : x) }))}
                      type="number" step="0.05" min="0" aria-label="Prix unitaire" style={{ ...INPUT, width: '96px', ...mono }} />
                  </div>
                ))}
                <button type="button" onClick={() => setEdit(prev => ({ ...prev, lignes: [...prev.lignes, { description: '', quantite: '1', prix_unitaire: '' }] }))}
                  className="src-row-hover" style={{ ...GHOST_BTN, alignSelf: 'flex-start', padding: '6px 10px', fontSize: '11.5px' }}>
                  <BI icon="plus-lg" style={{ fontSize: '10px' }} /> Ajouter une ligne
                </button>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '5px 14px', fontSize: '12.5px' }}>
              <span style={{ color: 'var(--text-2)' }}>Total HT</span><span className="ats-mono" style={{ textAlign: 'right', color: 'var(--text-strong)' }}>{chfAmt(totalHT)}</span>
              <span style={{ color: 'var(--text-2)' }}>{tvaTaux === 0 ? (f.tva_mention || 'TVA non applicable') : `TVA ${String(tvaTaux).replace('.', ',')} %`}</span>
              <span className="ats-mono" style={{ textAlign: 'right', color: 'var(--text-strong)' }}>{chfAmt(f.tva_montant ?? totalHT * (tvaTaux / 100))}</span>
              <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>Total TTC</span><span className="ats-mono" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-strong)' }}>{chfAmt(totalTTC)}</span>
            </div>
            {edit ? (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>Échéance
                  <input type="date" value={edit.echeance} onChange={e => setEdit(prev => ({ ...prev, echeance: e.target.value }))} style={{ ...INPUT, marginTop: '4px', ...mono }} />
                </label>
                <label style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>Note (imprimée sur la facture)
                  <input value={edit.note} onChange={e => setEdit(prev => ({ ...prev, note: e.target.value }))} placeholder="ex. Selon CRA validé du mois" style={{ ...INPUT, marginTop: '4px' }} />
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={enregistrer} disabled={saving} style={{ ...PRIMARY_BTN, opacity: saving ? 0.6 : 1 }}>
                    <BI icon={saving ? 'arrow-repeat' : 'check-lg'} style={saving ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Enregistrer les modifications
                  </button>
                  <button onClick={() => setEdit(null)} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '12px' }}>
                {brouillon ? (
                  <button onClick={commencerEdition} className="src-row-hover" style={GHOST_BTN}><BI icon="pencil" /> Modifier la facture</button>
                ) : (
                  <div style={{ fontSize: '11.5px', lineHeight: 1.5, color: 'var(--text-3)' }}>
                    Facture {sl.toLowerCase()} — les montants ne se modifient plus ici. Pour corriger, créez une nouvelle facture.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Actions ── */}
          <div style={{ ...CARD, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Où part la facture — informatif : l'adresse ne se demande
                QU'À L'ENVOI, dans le popup, et seulement si elle manque. */}
            {/* Qui encaisse : en affacturage, le client paie le facturier, pas
                nous. C'est écrit sur la facture — ça doit se lire ici aussi,
                avant l'envoi. En brouillon, le choix se fait à l'édition. */}
            {(() => {
              const idCourant = f.facturier_id ?? f.facturier?.id ?? null;
              const courant = f.facturier || facturiers.find(x => String(x.id) === String(idCourant)) || null;
              if (edit) {
                if (!facturiers.length) return null;
                return (
                  <label style={{ display: 'block' }}>
                    <div style={{ ...LBL, marginBottom: '5px' }}>Encaissement</div>
                    <select className="erp-field" style={{ width: '100%' }} value={edit.facturier_id ?? ''}
                      onChange={e => setEdit(v => ({ ...v, facturier_id: e.target.value }))}>
                      <option value="">Nos coordonnées bancaires</option>
                      {facturiers.map(x => <option key={x.id} value={x.id}>{x.nom}{x.actif ? ' (par défaut)' : ''}</option>)}
                    </select>
                  </label>
                );
              }
              if (!courant) return null;
              return (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: 'var(--text-2)' }}>
                  <BI icon="bank" style={{ fontSize: '11px', flex: 'none', marginTop: '2px' }} />
                  <span style={{ minWidth: 0 }}>
                    Encaissé par <b style={{ color: 'var(--text-strong)' }}>{courant.nom}</b>
                    {courant.iban && <span className="ats-mono" style={{ display: 'block', marginTop: '2px', fontSize: '11px' }}>{String(courant.iban).replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()}</span>}
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '11px', color: 'var(--text-3)' }}>Le client règle sur ce compte — la mention de cession figure sur la facture.</span>
                  </span>
                </div>
              );
            })()}
            {destinataire ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-2)' }}>
                <BI icon="envelope" style={{ fontSize: '11px', flex: 'none' }} />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Part à <b style={{ color: 'var(--text-strong)' }}>{destinataire.nom || destinataire.email}</b>
                  {destinataire.nom && destinataire.email && <span className="ats-mono" style={{ marginLeft: '6px', fontSize: '11px' }}>{destinataire.email}</span>}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: '11.5px', lineHeight: 1.5, color: 'var(--text-3)' }}>
                {destinataireMotif || "Aucun email de facturation sur la fiche client — il vous sera demandé à l'envoi, puis retenu pour les prochains."}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={envoyer} disabled={sending}
              style={brouillon ? { ...PRIMARY_BTN, opacity: sending ? 0.6 : 1 } : { ...GHOST_BTN, opacity: sending ? 0.6 : 1 }}>
              <BI icon={sending ? 'arrow-repeat' : 'send'} style={sending ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
              {f.statut === 'envoyee' || f.statut === 'en_retard' || f.statut === 'payee' ? 'Renvoyer au client' : 'Envoyer au client'}
            </button>
            <button onClick={() => onDownload?.(f)} className="src-row-hover" style={GHOST_BTN}><BI icon="download" /> PDF</button>
            <div style={{ flex: 1 }} />
            <ErpiaSelect value={f.statut || 'brouillon'} onChange={e => onStatus?.(f, e.target.value)} title="Changer le statut"
              style={{ ...INPUT, width: 'auto', padding: '6px 9px', fontSize: '11.5px', flex: 'none' }}>
              {STATUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </ErpiaSelect>
            </div>
          </div>
        </div>

        {/* ── Popup d'envoi : l'adresse manquante se saisit ICI, au moment de
            l'envoi, puis est retenue sur la fiche client. ── */}
        {popup && (
          <div onClick={() => setPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Envoyer la facture au client"
              style={{ width: 'min(430px, 94vw)', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '13px', padding: '18px 20px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)' }}>
              <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '16px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Envoyer la facture au client</div>
              <div style={{ marginTop: '4px', fontSize: '12.5px', lineHeight: 1.5, color: 'var(--text-2)' }}>
                {f.numero || `Facture #${f.id}`} · {chfAmt(totalTTC)} · {f.client || 'client'} — le PDF part avec les coordonnées de paiement.
              </div>
              <div style={{ marginTop: '13px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                <label style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>Email de facturation
                  <input ref={emailRef} type="email" value={popup.a} aria-invalid={destErreur?.champ === 'a' || undefined}
                    onChange={e => { setPopup(pv => ({ ...pv, a: e.target.value })); if (destErreur?.champ === 'a') setDestErreur(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') envoyerDepuisPopup(); }}
                    placeholder="compta@client.ch"
                    style={{ ...INPUT, marginTop: '4px', ...mono, fontSize: '12.5px', ...(destErreur?.champ === 'a' ? { borderColor: 'var(--danger-bd)' } : {}) }} />
                  {destErreur?.champ === 'a' && <span role="alert" style={{ display: 'block', marginTop: '4px', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--danger-fg)' }}>{destErreur.message}</span>}
                </label>
                <label style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>En copie — optionnel, séparées par des virgules
                  <input ref={ccRef} value={popup.cc} aria-invalid={destErreur?.champ === 'cc' || undefined}
                    onChange={e => { setPopup(pv => ({ ...pv, cc: e.target.value })); if (destErreur?.champ === 'cc') setDestErreur(null); }}
                    placeholder="achats@client.ch, direction@client.ch"
                    style={{ ...INPUT, marginTop: '4px', ...mono, fontSize: '12.5px', ...(destErreur?.champ === 'cc' ? { borderColor: 'var(--danger-bd)' } : {}) }} />
                  {destErreur?.champ === 'cc' && <span role="alert" style={{ display: 'block', marginTop: '4px', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--danger-fg)' }}>{destErreur.message}</span>}
                </label>
                {clientFiche && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '11.5px', lineHeight: 1.45, color: 'var(--text-2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={popup.retenir} onChange={e => setPopup(pv => ({ ...pv, retenir: e.target.checked }))} style={{ margin: '2px 0 0' }} />
                    <span>Retenir cette adresse sur la fiche de {clientFiche.nom} (contact « Reçoit les factures ») — plus jamais à retaper.</span>
                  </label>
                )}
              </div>
              <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => setPopup(null)} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
                <button onClick={envoyerDepuisPopup} disabled={sending} style={{ ...PRIMARY_BTN, opacity: sending ? 0.6 : 1 }}>
                  <BI icon={sending ? 'arrow-repeat' : 'send'} style={sending ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Envoyer au client
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
